import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { completeProject, getDashboard, getProject } from '../api';
import type { CompleteProjectPayload, Project, UnitPreference } from '../types';
import { Spinner } from '../components/Spinner';
import { formatWeight } from '../units';

// ProjectComplete — slice C/D completion-moment screen (briefs/complete-
// project-design.md Section 3). One route serves both the active-fork
// state (user picks Maintenance / New project / Coast) and the read-only
// recap state (project already completed or cancelled). Idempotent —
// reload after fork pick shows the recap; reload of a cancelled project
// shows the cancellation recap.
//
// Header tone branches status-first to prevent the cancellation-after-
// target-hit collision when slice E (S12+) starts writing
// target_first_hit_at. A cancelled project that hit target before
// cancellation stays neutral; only completed + target_first_hit_at IS
// NOT NULL earns the celebration copy.

type NextAction = CompleteProjectPayload['next_action'];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const GOAL_TITLE: Record<Project['goal'], string> = {
  fat_loss: 'Fat loss',
  recomp: 'Body recomposition',
  muscle_gain: 'Muscle gain',
  maintenance: 'Maintenance',
};

const RECOMP_POPULATION_LABEL: Record<NonNullable<Project['recomp_population']>, string> = {
  beginner: 'Beginner',
  returning: 'Returning lifter',
  high_bf: 'Higher body fat',
  override: 'Override',
};

const FORK_OPTIONS: ReadonlyArray<{
  value: NextAction;
  eyebrow: string;
  title: string;
  blurb: string;
}> = [
  {
    value: 'maintenance',
    eyebrow: 'Lock it in',
    title: 'Maintenance',
    blurb: 'Hold what you built. Coach voice softens for 28 days.',
  },
  {
    value: 'new_project',
    eyebrow: 'Next chapter',
    title: 'New project',
    blurb: 'Pick a new goal. Coach remembers what you just did.',
  },
  {
    value: 'coast',
    eyebrow: 'Take a breather',
    title: 'Coast',
    blurb: "Step back for 28 days. Fewer prompts. We're still here.",
  },
];

function pickHeader(project: Project): { eyebrow: string; title: string } {
  if (project.status === 'cancelled') {
    return { eyebrow: 'Project closed', title: 'Project cancelled.' };
  }
  if (project.status === 'completed') {
    if (project.target_first_hit_at !== null) {
      return { eyebrow: 'Target hit', title: 'You did it.' };
    }
    return { eyebrow: 'Project closed', title: 'Project complete.' };
  }
  // active — fork moment, celebrate the choice not the outcome
  return { eyebrow: "You're at the moment", title: 'Project complete.' };
}

function daysElapsed(startedAtIso: string, endIso: string | null): number {
  const start = new Date(startedAtIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  return Math.max(0, Math.floor((end - start) / 86_400_000));
}

export function ProjectComplete() {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();

  const [project, setProject] = useState<Project | null>(null);
  const [currentWeightKg, setCurrentWeightKg] = useState<number | null>(null);
  const [unitPref, setUnitPref] = useState<UnitPreference>('metric');
  const [error, setError] = useState<string | null>(null);

  const [selectedFork, setSelectedFork] = useState<NextAction | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!routeId) {
      setError('Missing project id');
      return;
    }
    let cancelled = false;
    setError(null);
    // Parallel fetch: project row drives state + recap card content;
    // dashboard analytics supplies current weight for the weight-delta tile.
    // Dashboard failure is non-fatal — the rest of the recap still renders.
    Promise.all([
      getProject(routeId),
      getDashboard().catch(() => null),
    ])
      .then(([projectRes, dashboardRes]) => {
        if (cancelled) return;
        setProject(projectRes.project);
        if (dashboardRes) {
          setCurrentWeightKg(dashboardRes.analytics.current_weight_kg);
          setUnitPref(dashboardRes.profile?.unit_preference ?? 'metric');
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load project');
      });
    return () => {
      cancelled = true;
    };
  }, [routeId]);

  const onConfirm = async (): Promise<void> => {
    if (!project || !selectedFork) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await completeProject(project.id, selectedFork);
      // Routing per fork — maintenance/coast back to dashboard so the
      // user sees the banner; new_project to the wizard for a fresh start.
      navigate(selectedFork === 'new_project' ? '/projects/new' : '/dashboard');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to complete project');
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="shell">
        <div className="errorbar">{error}</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="center">
        <Spinner />
      </div>
    );
  }

  const header = pickHeader(project);
  const isReadOnly = project.status !== 'active';
  const days = daysElapsed(project.started_at, project.completed_at);
  const weightDelta =
    currentWeightKg !== null
      ? +(currentWeightKg - project.starting_weight_kg).toFixed(1)
      : null;
  const weightDeltaLabel =
    weightDelta === null
      ? '—'
      : `${weightDelta >= 0 ? '+' : ''}${formatWeight(Math.abs(weightDelta), unitPref)}`;

  return (
    <div className="shell">
      <header className="row row--between rise" style={{ marginBottom: 8 }}>
        <div>
          <p className="eyebrow">{header.eyebrow}</p>
          <h1 className="h1">{header.title}</h1>
        </div>
      </header>

      <section className="section rise">
        <div className="card card--elevated">
          <p className="eyebrow">Project</p>
          <p className="h3" style={{ marginTop: 4 }}>{GOAL_TITLE[project.goal]}</p>
          {project.goal === 'recomp' && project.recomp_population !== null ? (
            <p className="muted" style={{ marginTop: 4, fontSize: 13 }}>
              Population: {RECOMP_POPULATION_LABEL[project.recomp_population]}
            </p>
          ) : null}
          {project.target_weight_kg !== null ? (
            <p className="muted" style={{ marginTop: 6, fontSize: 14, lineHeight: 1.55 }}>
              Started at {formatWeight(project.starting_weight_kg, unitPref)} → target {formatWeight(project.target_weight_kg, unitPref)}.
            </p>
          ) : (
            <p className="muted" style={{ marginTop: 6, fontSize: 14, lineHeight: 1.55 }}>
              Started at {formatWeight(project.starting_weight_kg, unitPref)}.
            </p>
          )}
          <p className="muted" style={{ marginTop: 4, fontSize: 13 }}>
            {fmtDate(project.started_at)} → {project.completed_at ? fmtDate(project.completed_at) : 'today'}
          </p>

          <div className="stat-grid" style={{ marginTop: 16 }}>
            <div className="stat">
              <div className="stat__label">Days</div>
              <div className="stat__value">{days}</div>
              <div className="stat__delta">since you started</div>
            </div>
            <div className="stat">
              <div className="stat__label">Weight change</div>
              <div className="stat__value" style={{ fontSize: 22 }}>{weightDeltaLabel}</div>
              <div className="stat__delta">
                {currentWeightKg === null ? 'No recent weight on file' : 'vs starting weight'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {!isReadOnly ? (
        <>
          <section className="section rise">
            <h2 className="h2">What's next?</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {FORK_OPTIONS.map((opt) => {
                const isSelected = selectedFork === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`card card--interactive${isSelected ? ' card--elevated' : ''}`}
                    onClick={() => setSelectedFork(opt.value)}
                    disabled={submitting}
                    style={{
                      textAlign: 'left',
                      width: '100%',
                      borderColor: isSelected ? 'var(--accent, currentColor)' : undefined,
                    }}
                    aria-pressed={isSelected}
                  >
                    <p className="eyebrow">{opt.eyebrow}</p>
                    <p className="h3" style={{ marginTop: 4 }}>{opt.title}</p>
                    <p className="muted" style={{ marginTop: 6, fontSize: 13, lineHeight: 1.5 }}>
                      {opt.blurb}
                    </p>
                  </button>
                );
              })}
            </div>
          </section>

          {submitError ? (
            <section className="section">
              <div className="errorbar">{submitError}</div>
            </section>
          ) : null}

          <section className="section rise">
            <button
              type="button"
              className="btn btn--full"
              onClick={() => void onConfirm()}
              disabled={selectedFork === null || submitting}
            >
              {submitting ? <><Spinner /> Confirming…</> : 'Confirm'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--full"
              onClick={() => navigate('/dashboard')}
              disabled={submitting}
              style={{ marginTop: 8 }}
            >
              Not yet
            </button>
          </section>
        </>
      ) : null}
    </div>
  );
}
